<template>
  <div ref="resizeRef" class="pt-8 overflow-hidden">
    <svg ref="svgRef" :id="id">
      <g>
        <g class="x-axis" v-if="axis === 'x' || axis === 'xy'" />
        <g class="y-axis" v-if="axis === 'y' || axis === 'xy'" />
      </g>
    </svg>
  </div>
</template>

<script>
import { onMounted, ref, watchEffect } from 'vue'
import {
  select,
  scaleBand,
  scaleLinear,
  axisBottom,
  axisLeft,
  max,
  min,
  easeCubic,
  format
} from 'd3'
import { tip } from 'd3-v6-tip'
import useResizeObserver from '@/tools/resizeObserver'

export default {
  name: 'BarChartGainLoss',
  props: ['id', 'data', 'axis', 'xLabel', 'yLabel', 'xData'],
  setup(props) {
    const svgRef = ref(null)
    const { resizeRef, resizeState } = useResizeObserver()

    onMounted(() => {
      watchEffect(() => {
        let { width, height } = resizeState.dimensions
        let maxw = 0
        let maxh = 0
        const margin = { top: 20, right: 20, bottom: 40, left: 40 }
        const formatPercent = format('.0%')

        width = width - margin.left - margin.right
        height = 350 - margin.top - margin.bottom

        const svg = select(svgRef.value)
          .style('background-color', 'white')
          .attr('width', width + margin.left + margin.right)
          .attr('height', height + margin.top + margin.bottom)
          .attr(
            'transform',
            'translate(' + margin.left + ',' + margin.top + ')'
          )

        svg
          .select('.y-axis')
          .selectAll('text')
          .each(function () {
            if (this.getBBox().width > maxw) maxw = this.getBBox().width
          })

        svg
          .select('.x-axis')
          .selectAll('text')
          .each(function () {
            if (this.getBBox().width > maxh) maxh = this.getBBox().width
          })

        const xScale = scaleBand()
          .domain(props.data.map(data => data.name))
          .rangeRound([0, width - maxw])
          .padding(0.1)

        const minData = min(props.data, v => {
          return Math.min(v.value)
        })

        const maxData = max(props.data, v => {
          return Math.max(v.value)
        })

        const getMinData = () => {
          if (minData * -1 < maxData) {
            return maxData * -1
          } else {
            return minData
          }
        }

        const getMaxData = () => {
          if (minData * -1 < maxData) {
            return maxData
          } else {
            return minData * -1
          }
        }

        const yScale = scaleLinear()
          .domain([getMinData(), getMaxData()])
          .rangeRound([height, 0])

        const xAxis = axisBottom(xScale)
        const yAxis = axisLeft(yScale)

        const tooltip = tip()
          .attr('class', 'd3-tip')
          .offset([-10, 0])
          .html((e, v) => {
            return formatPercent(v.value)
          })

        svg.call(tooltip)

        // Refresh Graph
        svg.selectAll('rect').remove()
        svg.selectAll('.x-label').remove()
        svg.selectAll('.y-label').remove()

        const barGraph = svg
          .append('g')
          .selectAll('.bar')
          .data(props.data)
          .enter()
          .append('rect')
          .classed('bar', true)
          .style('fill', v => {
            if (v.value < 0) {
              return '#e0e0e0'
            } else {
              return '#2e4faa'
            }
          })
          .attr('width', xScale.bandwidth())
          .attr('height', 0)
          .attr('x', v => xScale(v.name))
          .attr('y', () => yScale(0))
          .on('mouseover', tooltip.show)
          .on('mouseout', tooltip.hide)

        // set animation
        barGraph
          .transition()
          .ease(easeCubic)
          .duration(800)
          .attr('y', v => {
            if (v.value > 0) {
              return yScale(v.value)
            } else {
              return yScale(0)
            }
          })
          .attr('height', v => {
            return Math.abs(yScale(v.value) - yScale(0))
          })
          .delay((v, i) => i * 5)

        // set axis
        svg
          .select('.x-axis')
          .attr('class', 'x-axis')
          .attr('transform', 'translate(0,' + height + ')')
          .call(xAxis)
          .selectAll('text')
          .style('text-anchor', 'start')
          .attr('dx', '10px')
          .attr('dy', '-2px')
          .attr('transform', 'rotate(65)')

        svg
          .select('.x-axis')
          .append('text')
          .attr('class', 'x-label')
          .attr('x', (width - margin.left) / 2)
          .attr('y', maxh + 10)
          .text(props.xLabel)

        svg
          .select('.y-axis')
          .call(yAxis.tickSize(-(width - maxw)).tickFormat(format('.0%')))
          .attr('class', 'y-axis')
          .append('text')
          .attr('class', 'y-label')
          .attr('transform', 'rotate(-90)')
          .attr('x', -(height - margin.top) / 2)
          .attr('y', -maxw - 10)
          .attr('dy', -16)
          .style('text-anchor', 'end')
          .text(props.yLabel)

        svg
          .attr('height', height + margin.bottom + maxh)
          .attr(
            'viewBox',
            `-${maxw + margin.left} 0 ${
              width + margin.left + margin.right + margin.top + margin.bottom
            } ${
              height +
              margin.top +
              margin.bottom +
              margin.left +
              margin.right +
              20
            }`
          )
      })
    })

    return { svgRef, resizeRef }
  }
}
</script>

<style lang="scss" scoped>
:deep(svg) {
  display: block;
  fill: none;
  stroke: none;
  width: 100%;
  height: 100%;
  overflow: visible !important;

  .bar {
    fill: $primary;

    &.primary {
      fill: $primary;
    }

    &.warning {
      fill: $warning;
    }

    &.success {
      fill: $success;
    }

    &.info {
      fill: $info;
    }
  }

  .y-axis,
  .x-axis {
    line,
    path {
      color: $grey-6;
    }

    line {
      stroke: lightgrey;
      opacity: 0.7;
    }
  }

  .y-label,
  .x-label {
    @apply text-sm font-semibold;
    fill: black;
  }
}
</style>
